// src/features/collaboration/components/NetworkSidebar.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Badge,
  Skeleton,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  Group as GroupIcon,
  Event as EventIcon
} from '@mui/icons-material';
import { THEME_COLORS } from '../../../styles/theme';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useNotifications } from '../../../contexts/NotificationsContext';
import { 
  acceptConnectionRequest, 
  ignoreConnectionRequest, 
  fetchConnections,
  fetchUserProfile 
} from '../services/collaborationService';

const NetworkSidebar = ({ onCreatePost }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [connections, setConnections] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const { notificationCounts } = useNotifications();

  const myNetworkCount = (notificationCounts?.connection || 0) + (notificationCounts?.activity || 0);
  const groupCount = notificationCounts?.group || 0;
  const eventCount = notificationCounts?.event || 0;
  
  useEffect(() => {
    const loadData = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }
      
      try {
        // Load independently so one failed request does not wipe the other.
        const [profileResult, connectionsResult] = await Promise.allSettled([
          fetchUserProfile(currentUser.uid),
          fetchConnections(currentUser.uid)
        ]);

        if (profileResult.status === 'fulfilled') {
          setUserProfile(profileResult.value || {});
        } else {
          console.error("Error loading user profile:", profileResult.reason);
          setUserProfile({});
        }

        if (connectionsResult.status === 'fulfilled') {
          setConnections(connectionsResult.value || []);
        } else {
          console.error("Error loading connections:", connectionsResult.reason);
          setConnections([]);
        }
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [currentUser]);
  
  // Safely get display name and initials
  const getDisplayName = (profile) => {
    if (!currentUser) return 'Guest User';
    if (!profile) return currentUser.displayName || 'User';
    if (profile.firstName && profile.lastName) return `${profile.firstName} ${profile.lastName}`;
    if (profile.name) return profile.name;
    if (profile.firstName) return profile.firstName;
    return currentUser.displayName || 'User';
  };

  const getInitial = (name) => {
    if (!name || typeof name !== 'string') return '';
    return name.charAt(0);
  };
  
  const pendingConnections = connections.filter(c => c && c.status === 'pending') || [];
  const activeConnections = connections.filter(c => c && c.status === 'active') || [];
  
  const handleAcceptConnection = async (connectionId) => {
    if (!connectionId) return;
    
    try {
      await acceptConnectionRequest(connectionId);
      // Update local state to reflect the change
      setConnections(prevConnections => 
        prevConnections.map(conn => {
          if (conn && conn.id === connectionId) {
            return { ...conn, status: 'active' };
          }
          return conn;
        })
      );
    } catch (error) {
      console.error("Error accepting connection:", error);
    }
  };
  
  const handleIgnoreConnection = async (connectionId) => {
    if (!connectionId) return;
    
    try {
      await ignoreConnectionRequest(connectionId);
      // Remove the connection from the local state
      setConnections(prevConnections => 
        prevConnections.filter(conn => conn && conn.id !== connectionId)
      );
    } catch (error) {
      console.error("Error ignoring connection:", error);
    }
  };
  
  const handleViewProfile = () => {
    navigate('/profile');
  };

  const isProfileIncomplete = Boolean(currentUser)
    && !loading
    && (!userProfile || !(
      (userProfile.firstName && userProfile.lastName) ||
      userProfile.name
    ));
  
  return (
    <Box>
      <Card elevation={2} sx={{ mb: 3, borderRadius: 2, position: 'relative' }}>
        {/* Background banner spanning the full width */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 80, // Adjust this value as needed for banner height
            bgcolor: THEME_COLORS.primary, 
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
          }}
        />
        <CardContent sx={{ pb: 1, pt: 5 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 2,
              position: 'relative',
            }}
          >
            {loading ? (
              <Skeleton variant="circular" width={80} height={80} />
            ) : (
              <Avatar
                src={userProfile?.profileImage || undefined}
                alt={getDisplayName(userProfile)}
                sx={{ 
                  width: 80, 
                  height: 80, 
                  mb: 1,
                  bgcolor: 'white',
                  border: '2px solid white', // Optional: adds a white border to accentuate the overlap
                }}
              >
                {getInitial(getDisplayName(userProfile))}
              </Avatar>
            )}

            {loading ? (
              <>
                <Skeleton width="60%" height={28} />
                <Skeleton width="40%" height={20} />
              </>
            ) : (
              <>
                <Typography variant="h6" fontWeight="bold" textAlign="center">
                  {getDisplayName(userProfile)}
                </Typography>
                <Typography variant="body2" color="textSecondary" textAlign="center">
                  {userProfile?.title || 'Sign in to view your profile'}
                </Typography>
              </>
            )}
          </Box>

          {isProfileIncomplete && (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" onClick={handleViewProfile}>
                  Complete
                </Button>
              }
            >
              Profile incomplete. Please finish your profile.
            </Alert>
          )}
          
          <Divider sx={{ mt: 2, mb: 2 }} />
          
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="body2" color="textSecondary">
              Profile Views
            </Typography>
            {loading ? (
              <Skeleton width="30%" height={32} />
            ) : (
              <Typography variant="h6" fontWeight="bold">
                {userProfile?.profileViews || 0}
              </Typography>
            )}
          </Box>
          
          <Box>
            <Typography variant="body2" color="textSecondary">
              Connections
            </Typography>
            {loading ? (
              <Skeleton width="20%" height={32} />
            ) : (
              <Typography variant="h6" fontWeight="bold">
                {activeConnections.length}
              </Typography>
            )}
          </Box>
          
          <Divider sx={{ mt: 2, mb: 2 }} />
          
          <Button
            fullWidth
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onCreatePost}
            disabled={!currentUser}
            sx={{ 
              backgroundColor: THEME_COLORS.primary,
              '&:hover': { backgroundColor: '#1e1c4e' },
              mb: 1.5
            }}
          >
            Create Post
          </Button>
          
          <Button
            fullWidth
            variant="outlined"
            onClick={handleViewProfile}
            disabled={!currentUser}
            sx={{ 
              borderColor: THEME_COLORS.primary,
              color: THEME_COLORS.primary,
              '&:hover': { 
                borderColor: '#1e1c4e',
                backgroundColor: 'rgba(39, 35, 97, 0.04)'
              }
            }}
          >
            View Profile
          </Button>
        </CardContent>
      </Card>
      
      {!loading && pendingConnections.length > 0 && (
        <Card elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold" mb={1}>
              Connection Requests
            </Typography>
            <List disablePadding>
              {pendingConnections.slice(0, 3).map((connection) => (
                <ListItem 
                  key={connection.id || 'no-id'} 
                  disablePadding 
                  sx={{ 
                    mb: 1.5,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start'
                  }}
                >
                  <Box display="flex" width="100%" mb={1}>
                    <ListItemAvatar sx={{ minWidth: 45 }}>
                      <Avatar 
                        src={connection.user?.profileImage || undefined}
                        alt={connection.user?.name || 'User'}
                        sx={{ width: 36, height: 36, bgcolor: THEME_COLORS.primary }}
                      >
                        {getInitial(connection.user?.name)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight="bold" noWrap>
                          {connection.user?.name || 'Unknown User'}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="textSecondary" noWrap>
                          {connection.user?.title || ''}
                        </Typography>
                      }
                    />
                  </Box>
                  <Box display="flex" width="100%" pl={5.5}>
                    <Button 
                      size="small" 
                      variant="contained"
                      onClick={() => handleAcceptConnection(connection.id)}
                      sx={{ 
                        mr: 1, 
                        backgroundColor: THEME_COLORS.primary,
                        '&:hover': { backgroundColor: '#1e1c4e' }
                      }}
                    >
                      Accept
                    </Button>
                    <Button 
                      size="small" 
                      variant="outlined"
                      onClick={() => handleIgnoreConnection(connection.id)}
                      sx={{ 
                        borderColor: THEME_COLORS.primary,
                        color: THEME_COLORS.primary,
                        '&:hover': { 
                          borderColor: '#1e1c4e',
                          backgroundColor: 'rgba(39, 35, 97, 0.04)'
                        }
                      }}
                    >
                      Ignore
                    </Button>
                  </Box>
                </ListItem>
              ))}
            </List>
            
            {pendingConnections.length > 3 && (
              <Button 
                fullWidth 
                onClick={() => navigate('/connections/requests')}
                sx={{ 
                  mt: 1,
                  color: THEME_COLORS.primary
                }}
              >
                View all ({pendingConnections.length})
              </Button>
            )}
          </CardContent>
        </Card>
      )}
      
      <Card elevation={2} sx={{ borderRadius: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold" mb={1}>
            Education Network
          </Typography>
          <List disablePadding>
            <ListItem
              button
              onClick={() => navigate('/connections')}
              disablePadding 
              sx={{ mb: 2 }}
            >
              <ListItemAvatar sx={{ minWidth: 40 }}>
                <Avatar sx={{ bgcolor: THEME_COLORS.primary, width: 32, height: 32 }}>
                  <PersonIcon fontSize="small" />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography variant="body2" fontWeight="normal">
                    My Network
                  </Typography>
                }
                secondary={
                  myNetworkCount > 0 ? (
                    <Badge
                      color="error"
                      badgeContent={myNetworkCount}
                      sx={{
                        '& .MuiBadge-badge': {
                          fontSize: '0.6rem',
                          height: 16,
                          minWidth: 16
                        }
                      }}
                    >
                      <Typography variant="caption" color="textSecondary">
                        New activity
                      </Typography>
                    </Badge>
                  ) : null
                }
              />
            </ListItem>
            
            <ListItem 
              button
              onClick={() => navigate('/districts')}
              disablePadding 
              sx={{ mb: 2 }}
            >
              <ListItemAvatar sx={{ minWidth: 40 }}>
                <Avatar sx={{ bgcolor: THEME_COLORS.primary, width: 32, height: 32 }}>
                  <SchoolIcon fontSize="small" />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography variant="body2" fontWeight="normal">
                    School Districts
                  </Typography>
                }
              />
            </ListItem>
            
            <ListItem 
              button
              onClick={() => navigate('/groups')}
              disablePadding 
              sx={{ mb: 2 }}
            >
              <ListItemAvatar sx={{ minWidth: 40 }}>
                <Avatar sx={{ bgcolor: THEME_COLORS.primary, width: 32, height: 32 }}>
                  <GroupIcon fontSize="small" />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography variant="body2" fontWeight="normal">
                    Groups
                  </Typography>
                }
                secondary={
                  groupCount > 0 ? (
                    <Badge 
                      color="error" 
                      badgeContent={groupCount} 
                      sx={{ 
                        '& .MuiBadge-badge': { 
                          fontSize: '0.6rem', 
                          height: 16, 
                          minWidth: 16 
                        } 
                      }}
                    >
                      <Typography variant="caption" color="textSecondary">
                        New activity
                      </Typography>
                    </Badge>
                  ) : null
                }
              />
            </ListItem>
            
            <ListItem 
              button
              onClick={() => navigate('/events')}
              disablePadding
            >
              <ListItemAvatar sx={{ minWidth: 40 }}>
                <Avatar sx={{ bgcolor: THEME_COLORS.primary, width: 32, height: 32 }}>
                  <EventIcon fontSize="small" />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography variant="body2" fontWeight="normal">
                    Events
                  </Typography>
                }
                secondary={
                  eventCount > 0 ? (
                    <Badge 
                      color="error" 
                      badgeContent={eventCount} 
                      sx={{ 
                        '& .MuiBadge-badge': { 
                          fontSize: '0.6rem', 
                          height: 16, 
                          minWidth: 16 
                        } 
                      }}
                    >
                      <Typography variant="caption" color="textSecondary">
                        New activity
                      </Typography>
                    </Badge>
                  ) : null
                }
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>
    </Box>
  );
};

export default NetworkSidebar;
